import { useState, useEffect, useMemo } from 'react';
import { useSchemaEntityTypes } from './useSchemaEntityTypes';
import { EntityCategorizationService } from '../services/EntityCategorizationService';
import type { EntityTypeInfo } from './useSchemaEntityTypes';

export type EntityCategory = 'person' | 'locality' | 'other' | 'meta' | 'utilities' | 'blacklist' | 'admin';

const ADMIN_ENTITY_TYPES = [
    'set',
    'policyrule',
    'workflow',
    'workflowactivity',
    'emailtemplate',
    'settransition',
    'requestapprovaltype',
    'journalentry',
    'modificationerror'
];

/** Minimal entity type shape for nav (name required; endpoint/schema optional). */
interface EntityTypeLike {
    name: string;
    endpoint?: string;
    schema?: string;
}

/**
 * Custom hook for managing entity categorization in navigation.
 * @param entitlementEntityIds - When provided (e.g. from Entitlements/entity), these IDs are merged into the entity list so Localities/Other/Admin/Meta sections have types to show even when schema is empty.
 */
export const useNavigationCategorization = (entitlementEntityIds?: string[]) => {
    const { entityTypes: schemaEntityTypes, isLoading: schemaEntityTypesLoading } = useSchemaEntityTypes();
    const [entityCategories, setEntityCategories] = useState<Map<string, EntityCategory>>(new Map());
    const [categorizationLoading, setCategorizationLoading] = useState(false);

    // Schema entity types + merge in entitlement IDs so nav sections have types to show when schema is empty
    const entityTypes = useMemo((): EntityTypeLike[] => {
        const base: EntityTypeLike[] = schemaEntityTypes as EntityTypeLike[];
        if (!entitlementEntityIds?.length) return base;
        const byName = new Map<string, EntityTypeLike>();
        base.forEach(t => byName.set(t.name, t));
        entitlementEntityIds.forEach(id => {
            if (!id || byName.has(id)) return;
            byName.set(id, {
                name: id,
                endpoint: `/api/scim/v2/${id}`,
                schema: `urn:dcps:scim:v1:${id}`
            });
        });
        return Array.from(byName.values());
    }, [schemaEntityTypes, entitlementEntityIds]);
    const isLoading = schemaEntityTypesLoading;

    // Categorize entities dynamically based on their schemas
    useEffect(() => {
        if (isLoading || !entityTypes.length) {
            return;
        }

        const categorizeEntities = async () => {
            setCategorizationLoading(true);
            const categorizationService = EntityCategorizationService.getInstance();

            try {
                // Batch categorize all entity types at once
                const entityTypeNames = entityTypes.map(type => type.name);
                const categoryResults = await categorizationService.categorizeEntityTypes(entityTypeNames);

                // Convert to the expected format
                const categories = new Map<string, EntityCategory>();
                categoryResults.forEach((category, entityType) => {
                    // Manually categorize Set, PolicyRule, Workflow, WorkflowActivity, EmailTemplate, SetTransition, and RequestApprovalType as admin
                    const normalizedType = entityType.toLowerCase();
                    if (ADMIN_ENTITY_TYPES.includes(normalizedType)) {
                        categories.set(entityType, 'admin');
                    } else {
                        categories.set(entityType, category.type);
                    }
                });

                setEntityCategories(categories);
            } catch (error) {
                console.error('Error in batch categorization:', error);
                // Set all as 'other' on error, but Set, PolicyRule, Workflow, WorkflowActivity, EmailTemplate, and SetTransition as admin
                const categories = new Map<string, EntityCategory>();
                entityTypes.forEach(type => {
                    const normalizedType = type.name.toLowerCase();
                    if (ADMIN_ENTITY_TYPES.includes(normalizedType)) {
                        categories.set(type.name, 'admin');
                    } else {
                        categories.set(type.name, 'other');
                    }
                });
                setEntityCategories(categories);
            } finally {
                setCategorizationLoading(false);
            }
        };

        categorizeEntities();
    }, [entityTypes, isLoading]);

    // Check if specific entity types exist to conditionally show navigation sections
    const hasGroups = useMemo(() =>
        entityTypes.some(type => type.name.toLowerCase() === 'group'),
        [entityTypes]
    );

    const hasClasses = useMemo(() =>
        entityTypes.some(type => type.name.toLowerCase() === 'class'),
        [entityTypes]
    );

    const hasLocalities = useMemo(() =>
        entityTypes.some(type =>
            entityCategories.get(type.name) === 'locality' &&
            entityCategories.get(type.name) !== 'blacklist'
        ),
        [entityTypes, entityCategories]
    );

    const hasOther = useMemo(() =>
        entityTypes.some(type =>
            entityCategories.get(type.name) === 'other' &&
            entityCategories.get(type.name) !== 'blacklist' && // Must not be blacklisted
            type.name.toLowerCase() !== 'class' // Exclude classes since they have their own section
        ),
        [entityTypes, entityCategories]
    );

    const hasAdmin = useMemo(() =>
        entityTypes.some(type =>
            entityCategories.get(type.name) === 'admin' &&
            entityCategories.get(type.name) !== 'blacklist' // Must not be blacklisted
        ),
        [entityTypes, entityCategories]
    );

    const hasMeta = useMemo(() =>
        entityTypes.some(type =>
            entityCategories.get(type.name) === 'meta' &&
            entityCategories.get(type.name) !== 'blacklist' // Must not be blacklisted
        ),
        [entityTypes, entityCategories]
    );

    const hasUtilities = useMemo(() => true, []); // Utilities section is always available

    return {
        entityCategories,
        categorizationLoading,
        entityTypesLoading: isLoading,
        entityTypes,
        hasGroups,
        hasClasses,
        hasLocalities,
        hasOther,
        hasAdmin,
        hasMeta,
        hasUtilities
    };
};

